import type { CustomerRequestResultDto } from '../contracts/customer-auth.js';
import type { CustomerRequestDraft } from '../contracts/customer-services.js';
import { customerServiceDefinition } from '../contracts/customer-services.js';
import type { CustomerServiceSlug } from '../contracts/customer-services.js';
import type { TamoomehClient } from './client.js';
import { CustomerApiError, type FieldIssue } from './errors.js';

/**
 * The request flow, as a framework-agnostic state machine.
 *
 * The point of this file is requirement 6: *the request must survive
 * authentication*. Everything the customer typed lives here, in memory, and the
 * flow decides what happens when the customer turns out not to be signed in.
 *
 * The state machine is deliberately separated from rendering so that the hard
 * part — "what happens to the draft when authentication interrupts submission?"
 * — is unit-testable in Node with no DOM at all. The custom element is a thin
 * projection of `RequestFlowSnapshot`.
 *
 * ## Where the draft lives
 *
 * | Situation | Store | Why |
 * | --- | --- | --- |
 * | Tamoomeh (same-site) | `MemoryDraftStore` | Sign-in happens in-page; nothing needs to outlive the document. |
 * | Porabzar (SSO) | `SessionDraftStore` | Sign-in is a full-page cross-domain redirect, so the draft must outlive the document. |
 *
 * `SessionDraftStore` is opt-in, namespaced, tab-scoped and short-lived, and it
 * is never used for tokens — only for the customer's own words, which they typed
 * into a form on this very page. `localStorage` is not used at all: it would
 * outlive the tab, and a draft is not worth persisting across sessions.
 */

export type RequestFlowStatus =
  | 'idle'
  | 'editing'
  | 'authenticating'
  | 'submitting'
  | 'success'
  | 'error';

export interface RequestDraftValues {
  subject: string;
  details: string;
  answers: Record<string, string>;
}

export interface RequestFlowSnapshot {
  status: RequestFlowStatus;
  service: CustomerServiceSlug;
  draft: RequestDraftValues;
  /** Per-field validation problems, from the client or the server. */
  issues: readonly FieldIssue[];
  error: CustomerApiError | null;
  result: CustomerRequestResultDto | null;
  /** True when local validation finds no problem. */
  complete: boolean;
  /** True when the customer must sign in before this draft can be filed. */
  needsAuth: boolean;
}

/** Persisted envelope. `v` lets a future change refuse an old shape safely. */
export interface DraftEnvelope {
  v: 1;
  flowId: string;
  service: CustomerServiceSlug;
  draft: RequestDraftValues;
  /** Epoch milliseconds after which the envelope is ignored. */
  expiresAt: number;
  /**
   * True when the customer was mid-submit and left the page only to sign in.
   *
   * This is the flag that makes "continue without starting again" literal: after
   * the handoff returns, the widget sees it, and files the very same draft the
   * customer had already validated, without another tap.
   */
  pendingSubmit?: boolean;
}

export interface DraftStore {
  save(envelope: DraftEnvelope): void;
  load(): DraftEnvelope | null;
  clear(): void;
}

/** Default: nothing leaves the page. */
export class MemoryDraftStore implements DraftStore {
  private envelope: DraftEnvelope | null = null;

  save(envelope: DraftEnvelope): void {
    this.envelope = envelope;
  }

  load(): DraftEnvelope | null {
    return this.envelope;
  }

  clear(): void {
    this.envelope = null;
  }
}

/** Storage surface `SessionDraftStore` needs; injected so tests need no DOM. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const DRAFT_STORAGE_PREFIX = 'tamoomeh.draft.';
/** A draft is only worth resuming for a few minutes; the handoff is seconds long. */
export const DRAFT_TTL_MS = 15 * 60 * 1000;

/**
 * `sessionStorage`-backed draft, used only when the flow will cross a redirect.
 *
 * Tab-scoped, same-origin, short-lived, and cleared as soon as the request is
 * filed. It is a deliberate exception to "in-memory only", justified by the one
 * thing in-memory state cannot do: survive a cross-domain sign-in round trip.
 */
export class SessionDraftStore implements DraftStore {
  constructor(
    private readonly storage: StorageLike,
    private readonly key = `${DRAFT_STORAGE_PREFIX}active`,
    private readonly ttlMs = DRAFT_TTL_MS,
    private readonly now: () => number = () => Date.now(),
  ) {}

  save(envelope: DraftEnvelope): void {
    try {
      this.storage.setItem(this.key, JSON.stringify({ ...envelope, expiresAt: this.now() + this.ttlMs }));
    } catch {
      // Private mode / quota. Losing the draft is acceptable; breaking the page
      // is not, and the customer can simply retype two fields.
    }
  }

  load(): DraftEnvelope | null {
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(this.key);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<DraftEnvelope>;
      if (parsed.v !== 1 || !parsed.draft || typeof parsed.expiresAt !== 'number') {
        this.clear();
        return null;
      }
      if (parsed.expiresAt <= this.now()) {
        this.clear();
        return null;
      }
      return parsed as DraftEnvelope;
    } catch {
      this.clear();
      return null;
    }
  }

  clear(): void {
    try {
      this.storage.removeItem(this.key);
    } catch {
      // ignore
    }
  }
}

export interface RequestFlowOptions {
  client: TamoomehClient;
  service: CustomerServiceSlug;
  store?: DraftStore;
  /** Extra attribution recorded with the request. */
  onEvent?: (name: 'flow.state' | 'auth.required' | 'request.failed', detail?: Record<string, unknown>) => void;
}

export interface SubmitOutcome {
  /** True when submission was interrupted and the customer must sign in. */
  needsAuth: boolean;
  result?: CustomerRequestResultDto;
  error?: CustomerApiError;
}

type Listener = (snapshot: RequestFlowSnapshot) => void;

export class RequestFlow {
  private readonly client: TamoomehClient;
  private readonly store: DraftStore;
  private readonly listeners = new Set<Listener>();
  private readonly onEvent: RequestFlowOptions['onEvent'];
  private service: CustomerServiceSlug;
  private draft: RequestDraftValues = { subject: '', details: '', answers: {} };
  private status: RequestFlowStatus = 'idle';
  private issues: readonly FieldIssue[] = [];
  private error: CustomerApiError | null = null;
  private result: CustomerRequestResultDto | null = null;
  private flowId: string;
  /** Set while a submission is waiting for sign-in to finish. */
  private resumeAfterAuth = false;

  constructor(options: RequestFlowOptions) {
    this.client = options.client;
    this.service = options.service;
    this.store = options.store ?? new MemoryDraftStore();
    this.onEvent = options.onEvent;
    this.flowId = newFlowId();
  }

  get snapshot(): RequestFlowSnapshot {
    return {
      status: this.status,
      service: this.service,
      draft: this.draft,
      issues: this.issues,
      error: this.error,
      result: this.result,
      complete: this.validate().length === 0,
      needsAuth: this.resumeAfterAuth,
    };
  }

  get currentService(): CustomerServiceSlug {
    return this.service;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Point the flow at a different section, resetting a mismatched draft. */
  setService(slug: CustomerServiceSlug): void {
    if (slug === this.service) return;
    this.service = slug;
    this.draft = { subject: '', details: '', answers: {} };
    this.issues = [];
    this.error = null;
    this.result = null;
    this.status = 'idle';
    this.resumeAfterAuth = false;
    this.emit();
  }

  setField(name: 'subject' | 'details', value: string): void {
    this.draft = { ...this.draft, [name]: value };
    this.issues = this.issues.filter((issue) => issue.field !== name);
    if (this.status === 'idle' || this.status === 'success' || this.status === 'error') {
      this.status = 'editing';
      this.error = null;
    }
    this.emit();
  }

  setAnswer(name: string, value: string): void {
    this.draft = { ...this.draft, answers: { ...this.draft.answers, [name]: value } };
    this.issues = this.issues.filter((issue) => issue.field !== name);
    if (this.status === 'idle' || this.status === 'success' || this.status === 'error') {
      this.status = 'editing';
      this.error = null;
    }
    this.emit();
  }

  /** Local validation problems, computed from the shared service definition. */
  validate(): readonly FieldIssue[] {
    const draft: Partial<CustomerRequestDraft> = {
      service: this.service,
      subject: this.draft.subject,
      details: this.draft.details,
      answers: this.draft.answers,
    };
    return this.client.requests.validate(draft);
  }

  /** Server issues that name a field of this service's form, keyed by its name. */
  private formIssues(issues: readonly FieldIssue[]): FieldIssue[] {
    const fields = customerServiceDefinition(this.service)?.fields ?? [];
    const names = new Set(['subject', 'details', ...fields.map((field) => field.name)]);
    return issues
      .map((issue) => ({ ...issue, field: issue.field.replace(/^answers\./, '') }))
      .filter((issue) => names.has(issue.field));
  }

  fieldIssue(name: string): FieldIssue | null {
    return this.issues.find((issue) => issue.field === name) ?? null;
  }

  /** True when there is something worth preserving across a sign-in redirect. */
  hasDraftContent(): boolean {
    return Boolean(this.draft.subject.trim() || this.draft.details.trim() || Object.keys(this.draft.answers).length > 0);
  }

  /**
   * Persist the draft before leaving the page.
   *
   * Called by the element immediately before it navigates into the SSO handoff,
   * and only then. On the same-site path the draft never leaves memory.
   */
  persist(pendingSubmit = false): void {
    if (!this.hasDraftContent()) return;
    this.store.save({
      v: 1,
      flowId: this.flowId,
      service: this.service,
      draft: this.draft,
      expiresAt: Date.now() + DRAFT_TTL_MS,
      ...(pendingSubmit ? { pendingSubmit: true } : {}),
    });
  }

  /**
   * Restore a persisted draft.
   *
   * A draft for a different section is discarded rather than silently retargeted:
   * resuming a legal question into the sports section would file it against the
   * wrong service.
   */
  restore(): boolean {
    const envelope = this.store.load();
    if (!envelope) return false;
    if (envelope.service !== this.service) {
      this.store.clear();
      return false;
    }
    this.flowId = envelope.flowId;
    this.draft = {
      subject: envelope.draft.subject ?? '',
      details: envelope.draft.details ?? '',
      answers: envelope.draft.answers ?? {},
    };
    if (envelope.pendingSubmit) this.resumeAfterAuth = true;
    if (this.hasDraftContent()) this.status = 'editing';
    this.emit();
    return true;
  }

  /**
   * Submit the draft.
   *
   * Three outcomes:
   *  - the customer is signed in and the request is filed → `success`;
   *  - the customer is not signed in → the draft is kept, the flow moves to
   *    `authenticating` and `needsAuth: true` is returned, *without* clearing
   *    anything;
   *  - the server rejects it → `error` with the draft intact, so a retry does
   *    not cost the customer their typing.
   */
  async submit(): Promise<SubmitOutcome> {
    const issues = this.validate();
    if (issues.length > 0) {
      this.issues = issues;
      this.status = 'editing';
      this.error = null;
      this.emit();
      return {
        needsAuth: false,
        error: new CustomerApiError('VALIDATION_ERROR', 'اطلاعات درخواست کامل نیست.', {
          fieldIssues: issues,
        }),
      };
    }

    if (!this.client.auth.isAuthenticated) {
      this.resumeAfterAuth = true;
      this.status = 'authenticating';
      this.error = null;
      this.emit();
      this.onEvent?.('auth.required', { service: this.service });
      return { needsAuth: true };
    }

    this.resumeAfterAuth = false;
    this.status = 'submitting';
    this.error = null;
    this.emit();

    try {
      const result = await this.client.requests.create({
        service: this.service,
        subject: this.draft.subject,
        details: this.draft.details,
        answers: this.draft.answers,
        idempotencyKey: this.flowId,
      });
      this.result = result;
      this.issues = [];
      this.status = 'success';
      this.store.clear();
      this.emit();
      return { needsAuth: false, result };
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      this.error = normalized;
      this.issues = normalized.fieldIssues;
      // An expired session is not a dead end: the draft is kept and the flow
      // returns to the authentication step instead of showing a bare error.
      if (normalized.requiresAuth) {
        this.resumeAfterAuth = true;
        this.status = 'authenticating';
        this.emit();
        this.onEvent?.('auth.required', { service: this.service, reason: normalized.code });
        return { needsAuth: true };
      }
      // A rejection that names fields on this form is something the customer
      // can fix, so it goes back to the form with those fields marked.
      const formIssues = normalized.code === 'VALIDATION_ERROR' ? this.formIssues(normalized.fieldIssues) : [];
      if (formIssues.length > 0) {
        this.issues = formIssues;
        this.error = null;
        this.status = 'editing';
        this.emit();
        this.onEvent?.('request.failed', { service: this.service, code: normalized.code });
        return { needsAuth: false, error: normalized };
      }
      this.status = 'error';
      this.emit();
      this.onEvent?.('request.failed', { service: this.service, code: normalized.code });
      return { needsAuth: false, error: normalized };
    }
  }

  /**
   * Called once authentication has succeeded.
   *
   * If a submission was interrupted, it is retried with the *same* idempotency
   * key, so a double submit (for example the customer also pressed the button
   * again) cannot create two requests.
   */
  async resumeAfterAuthentication(): Promise<SubmitOutcome | null> {
    if (!this.resumeAfterAuth) {
      this.status = this.hasDraftContent() ? 'editing' : 'idle';
      this.emit();
      return null;
    }
    this.status = 'editing';
    this.emit();
    return this.submit();
  }

  /** Abandon the flow and clear any persisted draft. */
  reset(): void {
    this.draft = { subject: '', details: '', answers: {} };
    this.issues = [];
    this.error = null;
    this.result = null;
    this.status = 'idle';
    this.resumeAfterAuth = false;
    this.flowId = newFlowId();
    this.store.clear();
    this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot;
    for (const listener of this.listeners) listener(snapshot);
    this.onEvent?.('flow.state', { status: snapshot.status, service: snapshot.service });
  }
}

function newFlowId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') return `req-${cryptoObj.randomUUID()}`;
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The service definition a flow is filing under. */
export function flowServiceDefinition(flow: RequestFlow) {
  return customerServiceDefinition(flow.currentService);
}
